/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../../_metronic/layout/core/PageData'
import {FormikProvider, useFormik} from 'formik'
import { preventEnterSubmit } from '../../../../util/Functions'
import { array, object, string } from 'yup'
import { renderIdentifier } from '../../../../util/Constants'
import { SubSectionHeader } from '../../../../common/component/SubSectionHeader'
import { IAttachment } from '../../../media/Media.model'
import { MessageToast, MessageType } from '../../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { IContactDto } from '../../../contact/Contact.model'
import FormValidator from '../../../../util/FormValidator.util'
import { getStatusTypes, IATask, ITaskDto, ITaskUpdateRequestDto, TaskType } from '../Task.model'
import { useContactGetByIdMutation } from '../../../contact/Contact.api'
import { useTaskGetByIdMutation, useTaskMediaDeleteByIdMutation, useTaskUpdateByIdMutation } from '../Task.api'
import { Spinner } from '../../../../common/component/Spinner'
import { TicketEmbedered } from '../../ticket/components/TicketEmbedered'
import { CustomInputRenderer } from '../../../../common/component/CustomInputRenderer'
import { ContactPicker } from '../../../contact/components/ContactPicker'
import { ContactListItem } from '../../../contact/components/ContactListItem'
import { DateTimeInputPicker } from '../../../../common/component/DateTimeInputPicker'
import { FilePicker } from '../../../../common/component/FilePicker'
import { parseLocalDate } from '../../../../util/Locale'

interface IUpdateFormikSchema {
  createdAt?: string
  id: string
  statusCode: string
  status: string
  typeCode: string
  type: string
  title: string
  assignedTo?: string
  description?: string
  diagnostic?: string
  arrivalAddress?: string
  arrivedAt?: string
  resolution?: string
  resolvedAt?: string
  attachments: IAttachment[]
}

export const TaskEditPage = () => {
  const intl = useIntl()
  const formValidator = new FormValidator(intl)
  const navigate = useNavigate()
  const {ticketId, taskId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.TICKETS'}),
      path: '/tickets',
      isSeparator: false,
      isActive: false,
    },
    {
      title: `/`,
      path: ``,
      isSeparator: true,
      isActive: false,
    },
    {
      title: `${ticketId}`,
      path: `/tickets/${ticketId}`,
      isSeparator: false,
      isActive: false,
    },
  ]
  
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [validationSchema, setValidationSchema] = useState(
    object().shape({
      id: formValidator.validateStringStrict(),
      statusCode: formValidator.validateStringStrict(),
      status: formValidator.validateStringStrict(),
      typeCode: formValidator.validateStringStrict(),
      type: string().when('typeCode', {
        is: (value) => value === 'OTHER',
        then: () => formValidator.validateStringStrict(),
      }),
      title: formValidator.validateStringStrict(),
      assignedTo: formValidator.validateString(),
      description: formValidator.validateTextArea(),
      diagnostic: formValidator.validateTextArea(),
      arrivalAddress: formValidator.validateString(),
      arrivedAt: formValidator.validateString(),
      resolution: formValidator.validateTextArea(),
      resolvedAt: formValidator.validateString(),
      attachments: array().of(
        object({
          comments: formValidator.validateString(),
        })
      ),
    })
  )
  const [initialValues, setInitialValues] = useState<IUpdateFormikSchema>({
    id: taskId || '',
    statusCode: '',
    status: '',
    typeCode: TaskType.INCIDENT,
    type: intl.formatMessage({id: `DROPDOWN.TASK_TYPE.${TaskType.INCIDENT}`}),
    title: '',
    attachments: [],
  })

  const [contactGetById] = useContactGetByIdMutation()
  const [contact, setContact] = useState<IContactDto | undefined>(undefined) 
  const [isAttachmentProcessing, setIsAttachmentProcessing] = useState(false)

  const didFetchTask = useRef(false)
  const [task, setTask] = useState<ITaskDto | undefined>()
  const [isTaskLoading, setIsTaskLoading] = useState(true)
  const [taskGetById] = useTaskGetByIdMutation()
  const [taskUpdateById] = useTaskUpdateByIdMutation()

  const didTaskMediaRequestDelete = useRef(false)
  const [taskMediaDeleteById] = useTaskMediaDeleteByIdMutation()
  
  const formik = useFormik<IUpdateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)

          const request: ITaskUpdateRequestDto = {
            id: values.id,
            statusCode: values.statusCode,
            status: intl.formatMessage({
              id: `DROPDOWN.TASK.STATUS.${values.statusCode}`,
            }),
            typeCode: values.typeCode,
            type:
              values.typeCode === TaskType.OTHER
                ? values.typeCode.trim().toLocaleUpperCase()
                : intl.formatMessage({id: `DROPDOWN.TASK_TYPE.${values.typeCode}`}),
            title: values.title,
            assignedTo: values.assignedTo,
            description: values.description,
            diagnostic: values.diagnostic,
            arrivalAddress: values.arrivalAddress,
            arrivedAt: values.arrivedAt,
            resolution: values.resolution,
            resolvedAt: values.resolvedAt,
            attachments: values.attachments,
          }

          const response: any = await taskUpdateById(request).unwrap()
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/tickets/${ticketId}/tasks/${taskId}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchTaskData = async (id: string) => {
      try {
        if (!didFetchTask.current) {
          setIsTaskLoading(true)
          const response: any = await taskGetById({id})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            const data: IATask = response.data
            setTask(data.task)

            formik.setFieldValue('createdAt', data.task?.createdAt)
            formik.setFieldValue('id', data.task?.id)
            formik.setFieldValue('statusCode', data.task?.statusCode)
            formik.setFieldValue(
              'status',
              intl.formatMessage({
                id: `DROPDOWN.TASK.STATUS.${data.task?.statusCode}`,
              })
            )
            formik.setFieldValue('typeCode', data.task?.typeCode)
            formik.setFieldValue('type', data.task?.type)
            formik.setFieldValue('title', data.task?.title)
            formik.setFieldValue('description', data.task?.description)
            formik.setFieldValue('diagnostic', data.task?.diagnostic)
            formik.setFieldValue('assignedTo', data.task?.assignedTo?.id)
            formik.setFieldValue('arrivalAddress', data.task?.arrivalAddress)
            formik.setFieldValue('arrivedAt', data.task?.arrivedAt)
            formik.setFieldValue('resolution', data.task?.resolution)
            formik.setFieldValue('resolvedAt', data.task?.resolvedAt)
            formik.setFieldValue(
              'attachments',
              data.media.map((media) => ({
                createdAt: media.createdAt,
                id: media.id,
                fileName: media.fileName,
                temporalPath: media.temporalPath,
                path: media.path,
                size: media.size,
                contentType: media.contentType,
                comments: media.comments,
                status: media.status,
                processing: false,
                uploaded: true,
              }))
            )

            if (data.task?.assignedTo?.id) {
              fetchContact(data.task.assignedTo.id)
            }
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsTaskLoading(false)
      }
    }

    const fetchContact = async (id) => {
      const response: any = await contactGetById({id})
      if (response && response.data && !response.error) {
        const contactData: IContactDto = response.data
        setContact(contactData)
        formik.setFieldValue('reportedById', contactData.id)
      }
    }

    if (taskId) {
      fetchTaskData(taskId)
    }
  }, [])

  const contactSelectOnClickHandler = async (contact) => {
    if (contact) {
      setContact(contact)
      formik.setFieldValue('assignedTo', contact.id)
    }
  }

  const contactRemoveOnClickHandler = async () => {
    setContact(undefined)
    formik.setFieldValue('assignedTo', undefined)
  }

  const taskMediaDeleteOnClickHandler = async (taskId: string, id: string) => {
    try {
      if (id && !didTaskMediaRequestDelete.current) {
        const response: any = await taskMediaDeleteById({taskId, id}).unwrap()
        if (response && response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          formik.setFieldValue(
            'attachments',
            formik.values.attachments.filter((attachment) => attachment.id !== id)
          )
        }
      }
    } catch (e) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    }
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.id && formik.errors.id) ||
          (formik.touched.status && formik.errors.status) ||
          (formik.touched.typeCode && formik.errors.typeCode) ||
          (formik.touched.type && formik.errors.type) ||
          (formik.touched.title && formik.errors.title)
        )
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      {task && (
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 fs-2 fw-bolder'>{`${task.title}`}</span>
                          <span className='fw-bolder text-primary me-1 fs-5'>
                            {renderIdentifier(task.sequence)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      {task && (
                        <>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary me-2 d-none d-md-block'
                            onClick={() => formik.handleSubmit()}
                            disabled={isAttachmentProcessing || formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : (
                              <>
                                <i className='bi bi-floppy'></i>
                                <span className='indicator-label'>
                                  {intl.formatMessage({id: 'UTIL.SAVE'})}
                                </span>
                              </>
                            )}
                          </button>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                            onClick={() => formik.handleSubmit()}
                            disabled={isAttachmentProcessing || formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : (
                              <i className='bi bi-floppy'></i>
                            )}
                          </button>
                        </>
                      )}

                      {!(isAttachmentProcessing || formik.isSubmitting) && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/tickets/${ticketId}/tasks/${taskId}`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isTaskLoading && !formik.isSubmitting ? (
                <Spinner />
              ) : !task ? (
                <div className='row'>
                  <div className='d-flex flex-row-fluid flex-start mt-10'>
                    <span className='text-danger '>
                      <div className='d-flex flex-row justify-content-start align-items-center'>
                        <span className='fs-6 me-1'>
                          <i className='text-danger me-1 bi bi-exclamation-octagon' />
                          {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                        </span>
                      </div>
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                    <li className='nav-item'>
                      <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                        {tabWithError('kt_tab_pane_1') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                      </a>
                    </li>
                  </ul>
                  <div className='tab-content mt-10' id='myTabContent'>
                    <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                      {ticketId && (
                        <div className='row mb-5'>
                          <div className='px-5'>
                            <TicketEmbedered ticketId={ticketId} />
                          </div>
                        </div>
                      )}

                      <div className='row mb-5'>
                        <SubSectionHeader title={intl.formatMessage({id: 'UTIL.GENERAL'})} />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.CREATED_DATE'})}
                              value={
                                task.createdAt
                                  ? parseLocalDate(
                                      task.createdAt,
                                      intl.formatMessage({id: 'I18N.LOCALE'})
                                    )
                                  : ''
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                              value={
                                task.updatedAt
                                  ? parseLocalDate(
                                      task.updatedAt,
                                      intl.formatMessage({id: 'I18N.LOCALE'})
                                    )
                                  : ''
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'TICKET.TITLE'})}
                              required={true}
                              placeholder={intl.formatMessage({id: 'TICKET.TITLE'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName={`title`}
                              enableInputLength={true}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.STATUS'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='statusCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('statusCode')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {getStatusTypes(task.statusCode).map((status, index) => (
                                    <option key={index} value={status}>
                                      {intl.formatMessage({
                                        id: `DROPDOWN.TASK.STATUS.${status}`,
                                      })}
                                    </option>
                                  ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'TICKET.DESCRIPTION'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='description'
                              object={
                                <textarea
                                  rows={4}
                                  className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                  placeholder={intl.formatMessage({id: 'TICKET.DESCRIPTION'})}
                                  {...formik.getFieldProps('description')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                />
                              }
                              enableInputLength={true}
                              inputLength={3000}
                            ></CustomInputRenderer>

                            {!contact && (
                              <div className='row my-5'>
                                <ContactPicker
                                  label={intl.formatMessage({id: 'UTIL.ASSIGNED_TO'})}
                                  required={false}
                                  enabled={!formik.isSubmitting}
                                  selectOnClick={contactSelectOnClickHandler}
                                ></ContactPicker>
                              </div>
                            )}

                            {contact && (
                              <div className='row my-5'>
                                <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                  <span className='text-gray-700'>
                                    {intl.formatMessage({id: 'UTIL.ASSIGNED_TO'})}
                                  </span>
                                </label>

                                <div className='col-xxl-9'>
                                  <ContactListItem
                                    disabled={formik.isSubmitting}
                                    data={contact}
                                    rowSelected={contact.id}
                                    removeOnClick={contactRemoveOnClickHandler}
                                  ></ContactListItem>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.DIAGNOSTIC_AND_RESOLUTION'})}
                        />
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ARRIVAL_ADDRESS'})}
                              placeholder={intl.formatMessage({id: 'UTIL.ARRIVAL_ADDRESS'})}
                              required={false}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='arrivalAddress'
                              enableInputLength={true}
                            ></CustomInputRenderer>

                            <DateTimeInputPicker
                              label={intl.formatMessage({id: 'UTIL.ARRIVAL_DATE_TIME'})}
                              placeholder={intl.formatMessage({id: 'UTIL.ARRIVAL_DATE_TIME'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='arrivedAt'
                              minDateEnable={!!formik.values.createdAt}
                              minDate={((): string | undefined => {
                                if (formik.values.createdAt) {
                                  const createdAt = new Date(formik.values.createdAt)
                                  return new Date(
                                    createdAt.getFullYear(),
                                    createdAt.getMonth(),
                                    createdAt.getDate(),
                                    createdAt.getHours(),
                                    createdAt.getMinutes() + 1,
                                    0
                                  ).toUTCString()
                                }
                                return undefined
                              })()}
                              selectOnClickHandler={(date) => {
                                if (formik.values.createdAt) {
                                  const createdAt = new Date(formik.values.createdAt)
                                  if (date <= createdAt) {
                                    formik.setFieldValue('arrivedAt', '')
                                  }
                                }
                              }}
                            ></DateTimeInputPicker>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'TICKET.DIAGNOSTIC'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='diagnostic'
                              object={
                                <textarea
                                  rows={4}
                                  className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                  placeholder={intl.formatMessage({id: 'TICKET.DIAGNOSTIC'})}
                                  {...formik.getFieldProps('diagnostic')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                />
                              }
                              enableInputLength={true}
                              inputLength={3000}
                            ></CustomInputRenderer>

                            {/* <DateTimeInputPicker
                              label={intl.formatMessage({id: 'UTIL.RESOLUTION_DATE_TIME'})}
                              placeholder={intl.formatMessage({id: 'UTIL.RESOLUTION_DATE_TIME'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='resolvedAt'
                              minDateEnable={!!formik.values.createdAt}
                              minDate={((): string | undefined => {
                                if (formik.values.createdAt) {
                                  const createdAt = new Date(formik.values.createdAt)
                                  return new Date(
                                    createdAt.getFullYear(),
                                    createdAt.getMonth(),
                                    createdAt.getDate(),
                                    createdAt.getHours(),
                                    createdAt.getMinutes() + 1,
                                    0
                                  ).toUTCString()
                                }
                                return undefined
                              })()}
                              selectOnClickHandler={(date) => {
                                if (formik.values.createdAt) {
                                  const createdAt = new Date(formik.values.createdAt)
                                  if (date <= createdAt) {
                                    formik.setFieldValue('resolvedAt', '')
                                  }
                                }
                              }}
                            ></DateTimeInputPicker> */}

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'TICKET.RESOLUTION'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='resolution'
                              object={
                                <textarea
                                  rows={4}
                                  className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                  placeholder={intl.formatMessage({id: 'TICKET.RESOLUTION'})}
                                  {...formik.getFieldProps('resolution')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                />
                              }
                              enableInputLength={true}
                              inputLength={3000}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>

                      <div className='row mb-5'>
                        <SubSectionHeader title={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})} />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <FilePicker
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='attachments'
                              fileDeleteOnClickHandler={(id) =>
                                taskMediaDeleteOnClickHandler(task.id, id)
                              }
                              setIsAttachmentProcessing={setIsAttachmentProcessing}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}
